import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { useAppStore } from '@/stores/app';
import { api } from '@/lib/api';
import { LoadingSpinner } from '@/components/LoadingScreen';
import { AppIcon } from '@/components/AppIcon';

interface FamilyInfo {
  id: string;
  name: string;
  inviteCode: string;
}

interface FamilyMember {
  user_id: string;
  display_name: string;
  photo_url?: string;
  role: string;
}

interface GroupInfo {
  id: string;
  name: string;
  invite_code: string;
  role: string;
  member_count: number;
}

export default function GroupSettingsPage() {
  const navigate = useNavigate();
  const { user, familyId, setFamilyId, isPremium, setIsPremium } = useAuthStore();
  const { showToast } = useAppStore();

  const [isLoading, setIsLoading] = useState(true);
  const [family, setFamily] = useState<FamilyInfo | null>(null);
  const [members, setMembers] = useState<FamilyMember[]>([]);
  const [groups, setGroups] = useState<GroupInfo[]>([]);
  const [canCreateMore, setCanCreateMore] = useState(false);
  const [groupLimit, setGroupLimit] = useState(1);

  // 編集モーダル
  const [isEditNameModalOpen, setIsEditNameModalOpen] = useState(false);
  const [newFamilyName, setNewFamilyName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isSwitching, setIsSwitching] = useState<string | null>(null);

  const isOwner = members.find(m => m.user_id === user?.id)?.role === 'owner';

  useEffect(() => {
    loadData();
  }, [familyId]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      // 現在のグループ情報
      const familyRes = await api.getFamily();
      setFamily(familyRes.family);
      setMembers(familyRes.members || []);
      setNewFamilyName(familyRes.family?.name || '');

      // 所属グループ一覧
      const groupsRes = await api.getMyGroups();
      setGroups(groupsRes.groups);
      setCanCreateMore(groupsRes.canCreateMore);
      setGroupLimit(groupsRes.groupLimit);
      setIsPremium(groupsRes.isPremium);
    } catch (error) {
      console.error('Failed to load data:', error);
      showToast('データの読み込みに失敗しました', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSwitchGroup = async (groupId: string) => {
    if (groupId === familyId) return;

    setIsSwitching(groupId);
    try {
      const res = await api.switchGroup(groupId);
      setFamilyId(res.family.id);
      showToast(`「${res.family.name}」に切り替えました`, 'success');
      // データを再読み込み
      await loadData();
    } catch (error) {
      showToast('グループの切り替えに失敗しました', 'error');
    } finally {
      setIsSwitching(null);
    }
  };

  const handleAddGroup = () => {
    navigate('/family/setup?addNew=true');
  };

  // 招待URLを生成
  const getInviteUrl = (code: string) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/invite?code=${code}`;
  };

  // 招待URLを共有
  const handleShareInvite = async () => {
    if (!family?.inviteCode) return;

    const inviteUrl = getInviteUrl(family.inviteCode);
    const shareText = `シェア家計簿に招待されました！\n以下のリンクから参加してください：`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'シェア家計簿への招待',
          text: shareText,
          url: inviteUrl,
        });
      } catch (error) {
        console.log('Share cancelled');
      }
    } else {
      await navigator.clipboard.writeText(inviteUrl);
      showToast('招待URLをコピーしました', 'success');
    }
  };

  const handleCopyInviteUrl = () => {
    if (family?.inviteCode) {
      const inviteUrl = getInviteUrl(family.inviteCode);
      navigator.clipboard.writeText(inviteUrl);
      showToast('招待URLをコピーしました', 'success');
    }
  };

  const handleRefreshInviteCode = async () => {
    if (!confirm('招待コードを更新しますか？\n古いコードは使えなくなります。')) return;

    try {
      const res = await api.refreshInviteCode();
      setFamily((prev) => prev ? { ...prev, inviteCode: res.inviteCode } : null);
      showToast('招待コードを更新しました', 'success');
    } catch (error) {
      showToast('更新に失敗しました', 'error');
    }
  };

  const handleUpdateFamilyName = async () => {
    if (!newFamilyName.trim()) {
      showToast('グループ名を入力してください', 'error');
      return;
    }

    setIsSaving(true);
    try {
      await api.updateFamily({ name: newFamilyName });
      setFamily((prev) => prev ? { ...prev, name: newFamilyName } : null);
      setIsEditNameModalOpen(false);
      showToast('グループ名を更新しました', 'success');
    } catch (error) {
      showToast('更新に失敗しました', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveMember = async (memberId: string, memberName: string) => {
    if (!confirm(`${memberName}さんをグループから削除しますか？`)) return;

    try {
      await api.removeFamilyMember(memberId);
      setMembers(members.filter(m => m.user_id !== memberId));
      showToast('メンバーを削除しました', 'success');
    } catch (error) {
      showToast('削除に失敗しました', 'error');
    }
  };

  const handleLeaveGroup = async () => {
    if (!confirm('グループから退出しますか？\n登録した支払い情報は残ります。')) return;

    try {
      await api.leaveFamily();
      // 他のグループがあればそれに切り替え、なければsetup画面へ
      const otherGroups = groups.filter(g => g.id !== familyId);
      if (otherGroups.length > 0) {
        await handleSwitchGroup(otherGroups[0].id);
      } else {
        setFamilyId(null);
        navigate('/family/setup');
      }
      showToast('グループから退出しました', 'success');
    } catch (error) {
      showToast('退出に失敗しました', 'error');
    }
  };

  const handleDeleteGroup = async () => {
    if (!confirm('グループを削除しますか？\nこの操作は取り消せません。すべての支払い・精算データが削除されます。')) return;
    if (!confirm('本当に削除しますか？')) return;

    try {
      await api.deleteFamily();
      // 他のグループがあればそれに切り替え、なければsetup画面へ
      const otherGroups = groups.filter(g => g.id !== familyId);
      if (otherGroups.length > 0) {
        await handleSwitchGroup(otherGroups[0].id);
      } else {
        setFamilyId(null);
        navigate('/family/setup');
      }
      showToast('グループを削除しました', 'success');
    } catch (error) {
      showToast('削除に失敗しました', 'error');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (!family) {
    return (
      <div className="p-4 text-center">
        <p className="text-gray-500">グループ情報が見つかりません</p>
        <button
          onClick={() => navigate('/family/setup')}
          className="btn btn-primary btn-md mt-4"
        >
          グループを作成
        </button>
      </div>
    );
  }

  return (
    <div className="pb-4">
      {/* ヘッダー */}
      <div className="page-topbar">
        <div className="page-topbar-inner justify-start">
          <button
            onClick={() => navigate('/settings')}
            className="mr-3 text-gray-500"
          >
            ←
          </button>
          <h1 className="text-lg font-bold">グループ管理</h1>
        </div>
      </div>

      <div className="p-4 space-y-6">
        {/* グループ切り替え（複数グループがある場合または課金者の場合） */}
        {(groups.length > 1 || isPremium) && (
          <div className="card p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-medium text-gray-500">
                マイグループ ({groups.length}/{groupLimit})
              </h2>
              {isPremium && (
                <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">
                  <span className="inline-flex items-center gap-1"><AppIcon name="crown" size={12} />プレミアム</span>
                </span>
              )}
            </div>
            <div className="space-y-2">
              {groups.map((group) => (
                <button
                  key={group.id}
                  onClick={() => handleSwitchGroup(group.id)}
                  disabled={isSwitching !== null}
                  className={`w-full flex items-center justify-between p-3 rounded-lg border transition ${group.id === familyId
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                    }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${group.id === familyId ? 'bg-primary-500' : 'bg-gray-300'}`} />
                    <div className="text-left">
                      <p className="font-medium">{group.name}</p>
                      <p className="text-xs text-gray-500">
                        {group.member_count}人 · {group.role === 'owner' ? 'オーナー' : 'メンバー'}
                      </p>
                    </div>
                  </div>
                  {isSwitching === group.id && <LoadingSpinner />}
                  {group.id === familyId && isSwitching !== group.id && (
                    <span className="text-xs text-primary-500 font-medium">表示中</span>
                  )}
                </button>
              ))}
            </div>

            {canCreateMore && (
              <button
                onClick={handleAddGroup}
                className="w-full mt-3 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-primary-300 hover:text-primary-500 transition"
              >
                + グループを追加
              </button>
            )}
          </div>
        )}

        {/* グループ情報 */}
        <div className="card p-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-gray-500">現在のグループ</p>
              <p className="text-xl font-bold">{family.name}</p>
            </div>
            {isOwner && (
              <button
                onClick={() => setIsEditNameModalOpen(true)}
                className="btn btn-secondary btn-sm"
              >
                編集
              </button>
            )}
          </div>

          {isOwner && (
            <span className="badge badge-primary">オーナー</span>
          )}
        </div>

        {/* メンバー */}
        <div className="card p-4">
          <h2 className="text-sm font-medium text-gray-500 mb-3">
            メンバー ({members.length}/2)
          </h2>
          <div className="space-y-3">
            {members.map((member) => (
              <div key={member.user_id} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {member.photo_url ? (
                    <img
                      src={member.photo_url}
                      alt={member.display_name}
                      className="w-10 h-10 rounded-full"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-500">
                      <AppIcon name="user" size={19} />
                    </div>
                  )}
                  <div>
                    <p className="font-medium">{member.display_name}</p>
                    {member.role === 'owner' && (
                      <span className="text-xs text-primary-500">オーナー</span>
                    )}
                  </div>
                </div>
                {isOwner && member.user_id !== user?.id && (
                  <button
                    onClick={() => handleRemoveMember(member.user_id, member.display_name)}
                    className="text-red-500 text-sm"
                  >
                    削除
                  </button>
                )}
              </div>
            ))}
          </div>

          {members.length < 2 && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-sm text-gray-500 mb-3">パートナーを招待</p>
              <button
                onClick={handleShareInvite}
                className="btn btn-primary btn-md w-full mb-2"
              >
                <AppIcon name="share" size={18} />
                招待リンクを共有
              </button>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-gray-100 px-3 py-2 rounded font-mono text-sm">
                  {family.inviteCode}
                </code>
                <button
                  onClick={handleCopyInviteUrl}
                  className="btn btn-secondary btn-sm"
                >
                  <AppIcon name="copy" size={16} />
                </button>
                <button
                  onClick={handleRefreshInviteCode}
                  className="btn btn-secondary btn-sm"
                >
                  <AppIcon name="refresh" size={16} />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* 危険な操作 */}
        <div className="card p-4">
          <h2 className="text-sm font-medium text-red-500 mb-3">危険な操作</h2>

          {!isOwner ? (
            <button
              onClick={handleLeaveGroup}
              className="btn btn-danger btn-md w-full"
            >
              グループから退出
            </button>
          ) : (
            <button
              onClick={handleDeleteGroup}
              className="btn btn-danger btn-md w-full"
            >
              グループを削除
            </button>
          )}

          <p className="text-xs text-gray-500 mt-2">
            {isOwner
              ? 'グループを削除すると、すべてのデータが削除されます。'
              : '退出後は招待リンクで再参加できます。'
            }
          </p>
        </div>
      </div>

      {/* グループ名編集モーダル */}
      {isEditNameModalOpen && (
        <>
          <div className="modal-overlay" onClick={() => setIsEditNameModalOpen(false)} />
          <div className="modal-content p-6">
            <h2 className="text-lg font-bold mb-4">グループ名を編集</h2>

            <input
              type="text"
              value={newFamilyName}
              onChange={(e) => setNewFamilyName(e.target.value)}
              className="input mb-4"
              placeholder="グループ名"
              maxLength={30}
            />

            <div className="flex gap-3">
              <button
                onClick={() => setIsEditNameModalOpen(false)}
                className="btn btn-secondary btn-md flex-1"
              >
                キャンセル
              </button>
              <button
                onClick={handleUpdateFamilyName}
                disabled={isSaving || !newFamilyName.trim()}
                className="btn btn-primary btn-md flex-1"
              >
                {isSaving ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
